type SelectedLayoutIndex = number | null | undefined;
type ChipIndex = number | null | undefined;

export function getSmartChipNameFromSaveError(message: string): string | null {
  return /^Chip name "(.+)" (?:already exists|is already used)/i.exec(message)?.[1] ?? null;
}

export function isSmartChipNameSaveErrorForRow(
  message: string,
  chipName: string | null,
  chipId: number | null,
): boolean {
  const conflictingName = getSmartChipNameFromSaveError(message);
  return chipId == null && conflictingName != null && conflictingName.toLowerCase() === chipName?.trim().toLowerCase();
}

function normaliseSelectedLayoutIndex(index: SelectedLayoutIndex): number | null {
  return typeof index === 'number' && index >= 0 ? index : null;
}

export function normaliseSmartChipRunLogChipIndex(index: ChipIndex, tabTargetCount: number): number | null {
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) {
    return null;
  }
  if (tabTargetCount > 0 && index >= tabTargetCount) {
    return null;
  }
  return index;
}

export function resolveSmartChipRunLogChipIndex({
  persistedChipIndex,
  chipIndexFromChipId,
  tabTargetCount,
}: {
  persistedChipIndex: ChipIndex;
  chipIndexFromChipId: ChipIndex;
  tabTargetCount: number;
}): number | null {
  return (
    normaliseSmartChipRunLogChipIndex(persistedChipIndex, tabTargetCount) ??
    normaliseSmartChipRunLogChipIndex(chipIndexFromChipId, tabTargetCount)
  );
}

export function getLowestMissingCompleteSmartChipIndex({
  tabTargetCount,
  occupiedChipIndices,
  completeLayoutIndices,
}: {
  tabTargetCount: number;
  occupiedChipIndices: ReadonlySet<number>;
  completeLayoutIndices: ReadonlySet<number>;
}): number | null {
  return (
    Array.from({ length: Math.max(tabTargetCount, 0) }, (_value, index) => index).find(
      index => !occupiedChipIndices.has(index) && completeLayoutIndices.has(index),
    ) ?? null
  );
}

export function getSyncedSelectedLayoutIndex({
  currentSelectedLayoutIndex,
  persistedSelectedLayoutIndex,
  previousPersistedSelectedLayoutIndex,
  didWorkbookChange,
  tabCount,
}: {
  currentSelectedLayoutIndex: number;
  persistedSelectedLayoutIndex: SelectedLayoutIndex;
  previousPersistedSelectedLayoutIndex: SelectedLayoutIndex;
  didWorkbookChange: boolean;
  tabCount: number;
}): number {
  const maxIndex = Math.max(tabCount - 1, 0);
  const persisted = normaliseSelectedLayoutIndex(persistedSelectedLayoutIndex);

  if (didWorkbookChange) {
    return persisted == null ? 0 : Math.min(persisted, maxIndex);
  }

  if (persisted == null) {
    return Math.min(currentSelectedLayoutIndex, maxIndex);
  }

  const clampedPersisted = Math.min(persisted, maxIndex);
  const previousPersisted = normaliseSelectedLayoutIndex(previousPersistedSelectedLayoutIndex);
  const previousPersistedClamped = previousPersisted == null ? null : Math.min(previousPersisted, maxIndex);
  const shouldSyncToPersisted =
    previousPersistedClamped == null || currentSelectedLayoutIndex === previousPersistedClamped;

  if (currentSelectedLayoutIndex > maxIndex) {
    return clampedPersisted;
  }

  return shouldSyncToPersisted ? clampedPersisted : currentSelectedLayoutIndex;
}

export function canUseSmartChipRunTabDetails({
  canViewRun,
  isPlaceholder,
  isEnabled,
  layoutIsComplete,
}: {
  canViewRun: boolean;
  isPlaceholder: boolean;
  isEnabled: boolean;
  layoutIsComplete: boolean;
}): boolean {
  return canViewRun && (!isPlaceholder || isEnabled || layoutIsComplete);
}

export function canUploadSmartChipRunFilesForTab({
  isPlaceholder,
  isEnabled,
}: {
  isPlaceholder: boolean;
  isEnabled: boolean;
}): boolean {
  return !isPlaceholder || isEnabled;
}

export function canIncludeSmartChipRunLogInPayload({
  isPersistedRow,
  isCurrentSavedRow,
  isPendingSavedRow,
  tabCanPersistRunDetails,
  rowHasPersistableData,
}: {
  isPersistedRow: boolean;
  isCurrentSavedRow: boolean;
  isPendingSavedRow: boolean;
  tabCanPersistRunDetails: boolean;
  rowHasPersistableData: boolean;
}): boolean {
  if (!rowHasPersistableData) {
    return false;
  }
  if (isPersistedRow) {
    return true;
  }
  if (isPendingSavedRow) {
    return true;
  }
  return isCurrentSavedRow && tabCanPersistRunDetails;
}
